import { Vector3 } from "three/webgpu";
import type { NexusEdge } from "../../domain/mesh";
import type { PresentedNode } from "../../presentation/mesh";
import { projectCartographyPosition } from "../config/worldFrame";

const ITERATIONS = 12;
const MIN_SEPARATION_SYSTEM = 12.6;
const MIN_SEPARATION_DERIVED = 6.2;
const MAX_SEED_DRIFT = 2.6;
const SYSTEM_EXCLUSION_RADIUS = 15.5;
const DERIVED_EXCLUSION_RADIUS = 9.4;

export class CartographyLayoutEngine {
  private readonly delta = new Vector3();
  private readonly correction = new Vector3();

  resolve(nodes: PresentedNode[], edges: NexusEdge[]) {
    const seeds = new Map<string, Vector3>();
    const result = new Map<string, Vector3>();
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    for (const node of nodes) {
      const seed = node.archetype === "black-hole" ? new Vector3() : projectCartographyPosition(node.position);
      seeds.set(node.id, seed);
      result.set(node.id, seed.clone());
    }

    for (let iteration = 0; iteration < ITERATIONS; iteration += 1) {
      for (let leftIndex = 0; leftIndex < nodes.length; leftIndex += 1) {
        const leftNode = nodes[leftIndex];
        if (leftNode.archetype === "black-hole") continue;
        const left = result.get(leftNode.id)!;
        for (let rightIndex = leftIndex + 1; rightIndex < nodes.length; rightIndex += 1) {
          const rightNode = nodes[rightIndex];
          if (rightNode.archetype === "black-hole") continue;
          const right = result.get(rightNode.id)!;
          this.delta.copy(right).sub(left);
          const distance = Math.max(this.delta.length(), 0.001);
          const minimum =
            leftNode.kind === "system" && rightNode.kind === "system" ? MIN_SEPARATION_SYSTEM : MIN_SEPARATION_DERIVED;
          if (distance >= minimum) continue;
          const push = (minimum - distance) * 0.045;
          this.correction.copy(this.delta).multiplyScalar(push / distance);
          left.addScaledVector(this.correction, -0.5);
          right.addScaledVector(this.correction, 0.5);
        }
      }

      for (const edge of edges) {
        const sourceNode = nodesById.get(edge.source);
        const targetNode = nodesById.get(edge.target);
        if (!sourceNode || !targetNode) continue;
        const source = result.get(edge.source)!;
        const target = result.get(edge.target)!;
        if (sourceNode.kind === "system" && targetNode.kind !== "system")
          target.lerp(source, 0.006 + edge.strength * 0.004);
        if (targetNode.kind === "system" && sourceNode.kind !== "system")
          source.lerp(target, 0.006 + edge.strength * 0.004);
      }

      for (const node of nodes) {
        if (node.archetype === "black-hole") continue;
        const current = result.get(node.id)!;
        const seed = seeds.get(node.id)!;
        const exclusionRadius = node.kind === "system" ? SYSTEM_EXCLUSION_RADIUS : DERIVED_EXCLUSION_RADIUS;
        const centerDistance = current.length();
        if (centerDistance > 0.001 && centerDistance < exclusionRadius)
          current.multiplyScalar(exclusionRadius / centerDistance);
        this.delta.copy(current).sub(seed);
        const drift = this.delta.length();
        if (drift > MAX_SEED_DRIFT) current.copy(seed).addScaledVector(this.delta, MAX_SEED_DRIFT / drift);
        current.lerp(seed, node.kind === "system" ? 0.055 : 0.026);
      }
    }
    return result;
  }
}
